import { useState, useEffect, useCallback, useRef } from "react";
import { useAccount } from "wagmi";
import {
  bridgeAPI,
  mapApiStatusToUIStatus,
  getBridgeDirection,
  formatTokenAmount,
  formatTimestamp,
  type BridgeTransaction as ApiBridgeTransaction,
} from "@/lib/bridge/api";

// Interface for processed transactions compatible with existing UI
export interface ProcessedTransaction {
  id: string;
  direction: "camp-to-eth" | "eth-to-camp";
  amount: string;
  status:
    | "pending-hold"
    | "ready-for-execution"
    | "bridging"
    | "completed"
    | "failed";
  timestamp: Date;
  unlockTimestamp?: Date;
  txHash: string;
  isWhitelisted?: boolean;
  guid?: string;
  correlatedWith?: string;
  correlationMethod?: "guid" | "user-amount-time";
  // Token identification
  tokenSymbol?: string;
  tokenAddress?: string;
  tokenType?: "CAMP" | "ETH" | "USDC";
  // Deposit ID for release functionality
  depositId?: string;
}

interface ActivityPollingState {
  transactions: ProcessedTransaction[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  isPolling: boolean;
}

export function useActivityPollingAPI(intervalMs: number = 10000) {
  const { address, isConnected, status, connector } = useAccount();
  const [state, setState] = useState<ActivityPollingState>({
    transactions: [],
    isLoading: false,
    error: null,
    lastUpdated: null,
    isPolling: false,
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialLoadRef = useRef(true);
  const previousAddressRef = useRef<string | undefined>(address);

  // Helper to clear activity state in a single place
  const clearActivityState = useCallback(() => {
    setState({
      transactions: [],
      isLoading: false,
      error: null,
      lastUpdated: null,
      isPolling: false,
    });
  }, []);

  const processApiTransactions = useCallback(
    (apiTransactions: ApiBridgeTransaction[]): ProcessedTransaction[] => {
      return apiTransactions.map((tx) => {
        const direction = getBridgeDirection(
          tx.sourceChain,
          tx.destinationChain,
        );
        const status = mapApiStatusToUIStatus(tx.status, tx.overallStatus);
        const timestamp = formatTimestamp(tx.timestamp);

        // Choose the correct transaction hash based on direction and completion state
        const isCompleted = status === "completed";
        let txHash = tx.id;
        if (direction === "camp-to-eth") {
          // Origin: Camp, Destination: Ethereum
          txHash = isCompleted
            ? tx.ethereumTransaction?.transactionHash ||
              tx.campTransaction?.transactionHash ||
              tx.id
            : tx.campTransaction?.transactionHash ||
              tx.ethereumTransaction?.transactionHash ||
              tx.id;
        } else {
          // eth-to-camp. Origin: Ethereum, Destination: Camp
          txHash = isCompleted
            ? tx.campTransaction?.transactionHash ||
              tx.ethereumTransaction?.transactionHash ||
              tx.id
            : tx.ethereumTransaction?.transactionHash ||
              tx.campTransaction?.transactionHash ||
              tx.id;
        }

        // Calculate unlock timestamp for pending hold transactions
        let unlockTimestamp: Date | undefined;
        if (tx.timeRemaining && tx.timeRemaining > 0) {
          unlockTimestamp = new Date(Date.now() + tx.timeRemaining * 1000);
        }

        return {
          id: tx.id,
          direction,
          amount: formatTokenAmount(tx.amount, tx.tokenType),
          status,
          timestamp,
          unlockTimestamp,
          txHash,
          isWhitelisted: tx.isWhitelisted,
          guid: tx.guid,
          tokenSymbol: tx.tokenSymbol,
          tokenType: tx.tokenType,
          depositId: tx.depositId,
        };
      });
    },
    [],
  );

  const fetchTransactions = useCallback(
    async (isInitialLoad = false) => {
      if (!address || !isConnected) {
        setState((prev) => ({
          ...prev,
          transactions: [],
          isLoading: false,
          error: null,
        }));
        return;
      }

      setState((prev) => ({
        ...prev,
        isLoading: isInitialLoad,
        error: null,
      }));

      try {
        const response = await bridgeAPI.getUserTransactions(address, {
          // No limit - return all transactions
        });

        const processedTransactions = processApiTransactions(
          response.data.transactions,
        );

        // Sort by timestamp (most recent first)
        processedTransactions.sort(
          (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
        );

        setState((prev) => ({
          ...prev,
          transactions: processedTransactions,
          isLoading: false,
          error: null,
          lastUpdated: new Date(),
        }));
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        console.error("❌ [ActivityPollingAPI] Error:", errorMessage);

        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: `Failed to load transaction history: ${errorMessage}`,
        }));
      }
    },
    [address, isConnected, processApiTransactions],
  );

  // Start or stop polling based on connection status
  const startPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (address && isConnected) {
      // Initial load
      fetchTransactions(isInitialLoadRef.current);
      isInitialLoadRef.current = false;

      // Set up polling
      intervalRef.current = setInterval(() => {
        fetchTransactions(false);
      }, intervalMs);

      setState((prev) => ({ ...prev, isPolling: true }));
    }
  }, [address, isConnected, intervalMs, fetchTransactions]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setState((prev) => ({ ...prev, isPolling: false }));
  }, []);

  // Effect to immediately clear state when wallet address changes or disconnects
  useEffect(() => {
    const previousAddress = previousAddressRef.current;

    // If address changed from something to undefined (disconnected)
    // or changed to a different address (switched wallets)
    if (previousAddress && (!address || address !== previousAddress)) {
      clearActivityState();
      stopPolling();
    }

    // Update the ref for next comparison
    previousAddressRef.current = address;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, stopPolling]);

  // Effect to manage polling lifecycle
  useEffect(() => {
    if (address && isConnected) {
      startPolling();
    } else {
      stopPolling();
      isInitialLoadRef.current = true;
      // Clear transactions when wallet is disconnected
      clearActivityState();
    }

    return () => {
      stopPolling();
    };
  }, [address, isConnected, startPolling, stopPolling, clearActivityState]);

  // Effect: if wagmi status moves to disconnected, clear immediately
  useEffect(() => {
    if (status !== "connected") {
      stopPolling();
      isInitialLoadRef.current = true;
      clearActivityState();
    }
  }, [status, stopPolling, clearActivityState]);

  // Effect: listen to connector-level disconnect/change events (covers provider-driven disconnects)
  useEffect(() => {
    if (!connector) return;

    const handleDisconnect = () => {
      stopPolling();
      isInitialLoadRef.current = true;
      clearActivityState();
    };

    // Some connectors expose on/off event emitter APIs
    const anyConnector = connector as unknown as {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      on?: (event: string, listener: (...args: any[]) => void) => void;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      off?: (event: string, listener: (...args: any[]) => void) => void;
    };

    anyConnector.on?.("disconnect", handleDisconnect);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    anyConnector.on?.("change", (event: any) => {
      // If accounts array becomes empty, or account becomes undefined, treat as disconnect
      if (
        (event?.accounts &&
          Array.isArray(event.accounts) &&
          event.accounts.length === 0) ||
        event?.account === undefined
      ) {
        handleDisconnect();
      }
    });

    return () => {
      anyConnector.off?.("disconnect", handleDisconnect);
      // We cannot easily remove the specific change listener without the ref, so rely on disconnect handler
    };
  }, [connector, stopPolling, clearActivityState]);

  // Effect: attach EIP-1193 provider event listeners for instant reaction
  useEffect(() => {
    let cleanup: (() => void) | null = null;

    const attach = async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const provider: any = await (connector as any)?.getProvider?.();
        if (!provider?.on) return;

        const onAccountsChanged = (accounts: string[]) => {
          if (!accounts || accounts.length === 0) {
            stopPolling();
            isInitialLoadRef.current = true;
            clearActivityState();
          }
        };

        const onDisconnect = () => {
          stopPolling();
          isInitialLoadRef.current = true;
          clearActivityState();
        };

        provider.on("accountsChanged", onAccountsChanged);
        provider.on("disconnect", onDisconnect);

        cleanup = () => {
          provider.removeListener?.("accountsChanged", onAccountsChanged);
          provider.removeListener?.("disconnect", onDisconnect);
        };
      } catch {
        // ignore
      }
    };

    attach();

    return () => {
      if (cleanup) cleanup();
    };
  }, [connector, stopPolling, clearActivityState]);

  // Fallback: periodically verify provider accounts (covers edge cases where UI disconnect doesn't propagate)
  useEffect(() => {
    let verifyInterval: NodeJS.Timeout | null = null;

    const startVerify = async () => {
      // Only verify when we think we're connected
      if (!connector || !isConnected) return;

      const check = async () => {
        try {
          // Try to read accounts directly from provider
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const provider: any = await (connector as any).getProvider?.();
          const accounts: string[] | undefined = await provider?.request?.({
            method: "eth_accounts",
          });

          if (!accounts || accounts.length === 0) {
            // Treat as disconnected
            stopPolling();
            isInitialLoadRef.current = true;
            clearActivityState();
          }
        } catch {
          // Silently ignore provider errors
        }
      };

      verifyInterval = setInterval(check, 1000);
      // Run once immediately
      check();
    };

    startVerify();

    return () => {
      if (verifyInterval) clearInterval(verifyInterval);
    };
  }, [connector, isConnected, stopPolling, clearActivityState]);

  // Manual refresh function
  const refresh = useCallback(() => {
    fetchTransactions(true);
  }, [fetchTransactions]);

  return {
    ...state,
    refresh,
    startPolling,
    stopPolling,
  };
}
